/**
 * Privacy helpers applied to UI events before they reach the ring buffer (and so
 * the bug-report bundle and the telemetry stream). A URL fragment can carry a
 * capability secret, so every URL the client records keeps path + search only,
 * and free-text error messages are capped with any embedded URL replaced.
 */

/** Longest error text kept on an error event. */
export const ERROR_TEXT_MAX = 300;

/** Error events whose free-text fields may echo a URL from a failed request. */
const ERROR_EVENTS = new Set([
  "ui.error",
  "ui.unhandled_rejection",
  "ui.chat.error",
  "ui.forecast.error",
  "ui.resolver.failed",
]);

/** Fields on those events that hold free text. */
const ERROR_TEXT_FIELDS = ["message", "reason", "error", "last_error", "detail"];

// Brackets and parentheses are valid inside a URL (`http://[::1]/`, `/a(b)#x`), so the
// whole token up to whitespace or a quote is redacted.
const URL_RE = /\b[a-z][a-z0-9+.-]*:\/\/[^\s"'<>]+/gi;

/** `pathname + search` of a Location-like object, never its `hash`. */
export function pathAndSearch(loc) {
  if (!loc) return null;
  return `${loc.pathname || ""}${loc.search || ""}`;
}

/** Cap error text and replace every absolute URL in it with `[url]`. */
export function scrubErrorText(value, max = ERROR_TEXT_MAX) {
  if (value == null) return value;
  const text = String(value).replace(URL_RE, "[url]");
  return text.length > max ? text.slice(0, max) : text;
}

/** Path of a script URL (for `ui.error`'s `source`): no origin, query or fragment. */
export function scriptPath(value) {
  if (typeof value !== "string" || value === "") return value ?? null;
  try {
    return new URL(value, "http://x").pathname;
  } catch {
    return null;
  }
}

/**
 * Return the event with its error text scrubbed when it is one of the error
 * events; any other event is returned unchanged (same object).
 */
export function sanitizeUiEvent(event) {
  if (!event || !ERROR_EVENTS.has(event.event)) return event;
  const out = { ...event };
  for (const key of ERROR_TEXT_FIELDS) {
    if (typeof out[key] === "string") out[key] = scrubErrorText(out[key]);
  }
  if ("source" in out) out.source = scriptPath(out.source);
  return out;
}

/** Path + search of a recorded URL (absolute or relative), never its origin or fragment. */
export function urlPathAndSearch(value) {
  if (typeof value !== "string") return value ?? null;
  try {
    const u = new URL(value, "http://x");
    return `${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

/**
 * Sanitize an entry restored from storage, which an older build may have
 * recorded unscrubbed (e.g. a `ui.page.reload` url with its fragment).
 */
export function sanitizeRestoredEntry(entry) {
  const out = { ...sanitizeUiEvent(entry) };
  if ("url" in out) out.url = urlPathAndSearch(out.url);
  return out;
}

/**
 * The `view` ambient-context value: the route path plus the open side drawers,
 * e.g. `/` + `chat,log` → `"/|chat,log"`.
 */
export function telemetryView(pathname, drawers) {
  const open = Object.keys(drawers || {})
    .filter((k) => drawers[k])
    .sort();
  return `${pathname || "/"}|${open.join(",")}`;
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Long runs of id-ish characters with both a letter and a digit: secrets, tokens,
// capability ids. Plain words and short numbers pass.
const TOKEN_RE = /[A-Za-z0-9_-]{24,}/g;
// The second rule is the copy-link secret's own shape (32 random bytes, base64url: 43
// characters, often both cases and no digit), as the server's redaction checks it.
const looksLikeToken = (t) =>
  (/[0-9]/.test(t) && /[A-Za-z]/.test(t)) ||
  (t.length === 43 && /[A-Z]/.test(t) && /[a-z]/.test(t));

/**
 * Free text the UI shows (an element's accessible name, a console line) with every
 * email address replaced by `[email]`, token-shaped runs by `[token]` and absolute
 * URLs by `[url]`, capped at `max` characters.
 */
export function scrubFreeText(value, max) {
  if (value == null) return null;
  const text = String(value)
    .replace(URL_RE, "[url]")
    .replace(EMAIL_RE, "[email]")
    .replace(TOKEN_RE, (t) => (looksLikeToken(t) ? "[token]" : t));
  return text.length > max ? text.slice(0, max) : text;
}

const V8_FRAME = /^at\s+\S/;
const GECKO_FRAME = /^[^\s@]*@\S+:\d+(?::\d+)?$/;

const SCRIPT_PATH = /^\/assets\/|\.(?:m?js|jsx|tsx?)$/;

/**
 * A frame location URL as its path when it is this app's script (same origin, a script
 * asset; query and fragment dropped), else `[url]`: a message line shaped like a frame
 * can carry any URL.
 */
function frameLocation(u) {
  try {
    const url = new URL(u);
    const origin = typeof location !== "undefined" && location ? location.origin : null;
    if (origin && url.origin !== origin) return "[url]";
    // The URL token runs on into `:line:col` and a closing parenthesis.
    const m = /^(.*?)((?::\d+){1,2}\)?)?$/.exec(url.pathname);
    if (!SCRIPT_PATH.test(m[1])) return "[url]";
    // A query (`?v=…` in dev) swallows the `:line:col`; keep only that position.
    const pos = /((?::\d+){1,2}\)?)$/.exec(url.search + url.hash);
    return m[2] ? url.pathname : url.pathname + (pos ? pos[1] : "");
  } catch {
    return "[url]";
  }
}

/** Free text with script URLs as paths (others `[url]`), emails and tokens replaced. */
function scrubFramePart(text) {
  return text
    .replace(URL_RE, frameLocation)
    .replace(EMAIL_RE, "[email]")
    .replace(TOKEN_RE, (t) => (looksLikeToken(t) ? "[token]" : t));
}

/**
 * One stack frame (`at fn (url:l:c)` or `fn@url:l:c`), scrubbed: each absolute URL kept
 * as its path, emails and token-shaped runs replaced, capped at `max` characters. A
 * `fn@loc` frame is scrubbed on each side of its `@`, so the frame itself is never read
 * as an email.
 */
export function scrubFrame(line, max = 200) {
  const s = String(line);
  const at = GECKO_FRAME.test(s) ? s.indexOf("@") : -1;
  const text =
    at >= 0
      ? `${scrubFramePart(s.slice(0, at))}@${scrubFramePart(s.slice(at + 1))}`
      : scrubFramePart(s);
  return text.length > max ? text.slice(0, max) : text;
}

/**
 * The frame lines of a stack, scrubbed: only lines shaped like a frame (`at …`, or
 * `fn@loc:line[:col]`), so the lines of a multi-line error message never pass as
 * frames. `skip` drops that many leading frames, then at most `max` are kept.
 */
export function frameLines(stack, max = 5, skip = 0) {
  if (typeof stack !== "string") return [];
  return stack
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => V8_FRAME.test(l) || GECKO_FRAME.test(l))
    .slice(skip, skip + max)
    .map((l) => scrubFrame(l));
}
